import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, FileSearch, Handshake, Users, ScrollText, Shield } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { BOUNDARIES, CTA_LINE } from "@/lib/fsap-data";

const STEPS = [
  { icon: ClipboardCheck, title: "1. Discovery consultation", body: "30-minute call to understand the situation, deadlines, and what's already been tried. No cost, no obligation. If FSAP isn't the right fit, you get referrals." },
  { icon: FileSearch, title: "2. Case review & written recommendation", body: "Within a few business days you receive a written recommendation: retainer tier or à la carte, scope, communication standards, boundaries, and fees. Sliding scale or Compassion Plan eligibility reviewed here." },
  { icon: Handshake, title: "3. Engagement agreement", body: "Signed service agreement and payment set up. Sliding scale, scholarships, or fee waivers documented in the agreement addendum with clear duration and scope." },
  { icon: Users, title: "4. Advocacy in action", body: "Document prep, meeting attendance, agency navigation, and accompaniment on the schedule that fits your case. Written prep and recap for every meeting." },
  { icon: ScrollText, title: "5. Renewal or transition", body: "Retainers are re-evaluated each cycle. Cases wind down with a written transition plan so nothing is dropped." },
];

const BRING = [
  "Current IEP or 504 plan (or notice that one is needed)",
  "Any evaluations, FIEs, or diagnostic reports",
  "Recent school/agency correspondence and meeting notices",
  "Medical records relevant to accommodations (if applicable)",
  "Guardianship or POA documentation for adult clients",
];

const AdvocacyHowItWorks = () => (
  <div className="min-h-screen bg-bg-primary text-fg-primary">
    <PageMeta
      title="How FSAP Works — 5-Step Advocacy Process | Dr. Sully"
      description="Step-by-step FSAP process: discovery consultation, written case recommendation, engagement agreement, advocacy in action, and renewal. Clear boundaries and what to bring."
      path="/advocacy/how-it-works"
    />
    <SiteNav />
    <main>
      <section className="border-b border-fg-muted/10 bg-bg-secondary">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">
          <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-4">How It Works</p>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-3xl">
            Five steps from first call to advocacy in the room.
          </h1>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="space-y-8">
          {STEPS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="border border-fg-muted/15 p-8 flex gap-6">
              <Icon className="h-10 w-10 text-gold shrink-0" />
              <div>
                <h2 className="font-display text-2xl mb-2">{title}</h2>
                <p className="text-fg-secondary">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-fg-muted/10 bg-bg-secondary">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="font-display text-3xl mb-6">What to bring to your discovery call</h2>
          <ul className="space-y-2 max-w-2xl">
            {BRING.map((b) => <li key={b} className="text-sm text-fg-secondary">• {b}</li>)}
          </ul>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl mb-4">Boundaries</h2>
        <ul className="space-y-3 max-w-3xl">
          {BOUNDARIES.map((b) => (
            <li key={b} className="text-sm text-fg-secondary flex gap-3">
              <Shield className="h-4 w-4 text-gold shrink-0 mt-0.5" /><span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-fg-muted/10 bg-bg-secondary">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <p className="font-display text-2xl md:text-3xl max-w-3xl mx-auto">{CTA_LINE}</p>
          <Link
            to="/advocacy/intake"
            className="mt-8 inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 hover:brightness-110"
          >
            Start your intake <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
);

export default AdvocacyHowItWorks;
